/**
 * LA SUGERENCIA DE LA IA PARA UN PARTE (plan cultivos SIEX F7, 05-10-2026).
 *
 * Con la IA guiada por cultivo, el SERVIDOR resuelve la respuesta del modelo a una plaga de
 * la empresa y la guarda en `pest_photo_analyses.ai_pest_id` (nunca la escribe el modelo ni
 * el navegador). Aquí se lee esa fila (la del parte) y, si no la hay, lo que el parte guardó
 * en `ai_analysis.pestId` (la misma respuesta, copiada al parte al analizar), que lo pasa
 * quien llama: la ficha del parte ya lo tiene leído (sin otra consulta al parte).
 *
 * Es una SUGERENCIA etiquetada como de la IA (F6 de sanidad-focos): el triaje la enseña como
 * pastilla para elegirla con un toque, nunca la preselecciona por encima de lo que dijo una
 * persona (la pista «es lo mismo» y la voz del trabajador mandan) y nunca enlaza un foco (F1).
 * Solo con el interruptor `pest_crop_ai` encendido para la empresa.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { normalizarConfianza } from "@/lib/pestDiagnosis";

export interface AiSuggestion {
  pestId: string;
  /** 0–1. */
  confidence: number | null;
  /** listed · history · watch · company (de dónde salió en la lista). */
  aiOutcome: string | null;
  outsideList: boolean;
  analysisId: string | null;
  candidateMode: string | null;
}

/** Lo mínimo de una fila de `pest_photo_analyses` v2 (columnas nuevas: aún no en types.ts). */
interface FilaAnalisis {
  id: string;
  ai_pest_id: string | null;
  ai_outcome: string | null;
  candidate_mode: string | null;
  result: { data?: { confidence?: number; outsideList?: boolean } | null } | null;
}

type ConsultaSuelta = {
  from: (t: string) => {
    select: (c: string) => {
      eq: (k: string, v: string) => {
        not: (k: string, op: string, v: null) => {
          order: (k: string, o: { ascending: boolean }) => { limit: (n: number) => Promise<{ data: FilaAnalisis[] | null; error: unknown }> };
        };
      };
    };
  };
};

/** Pura: la sugerencia a partir de la fila guardada o del `ai_analysis` del parte. */
export function sugerenciaDe(fila: FilaAnalisis | null | undefined, aiAnalysis: unknown): AiSuggestion | null {
  if (fila?.ai_pest_id) {
    const c = fila.result?.data?.confidence;
    return {
      pestId: fila.ai_pest_id,
      confidence: typeof c === "number" ? normalizarConfianza(c) : null,
      aiOutcome: fila.ai_outcome ?? null,
      outsideList: fila.result?.data?.outsideList === true,
      analysisId: fila.id,
      candidateMode: fila.candidate_mode ?? null,
    };
  }
  const a = aiAnalysis as { pestId?: unknown; confidence?: unknown; outsideList?: unknown; outcome?: unknown; candidateMode?: unknown; provisional?: unknown } | null;
  if (!a || typeof a.pestId !== "string" || !a.pestId) return null;
  if (a.outcome && a.outcome !== "pest") return null;
  return {
    pestId: a.pestId,
    confidence: typeof a.confidence === "number" ? normalizarConfianza(a.confidence) : null,
    aiOutcome: null,
    outsideList: a.outsideList === true,
    analysisId: null,
    candidateMode: typeof a.candidateMode === "string" ? a.candidateMode : null,
  };
}

export function useAiSuggestion(sessionId: string | null, enabled: boolean, aiAnalysis: unknown = null) {
  const respaldo = sugerenciaDe(null, aiAnalysis);
  const query = useQuery({
    queryKey: ["ai-suggestion", sessionId],
    enabled: enabled && !!sessionId,
    staleTime: 30_000,
    retry: false,
    queryFn: async (): Promise<AiSuggestion | null> => {
      const sb = supabase as unknown as ConsultaSuelta;
      // La fila del análisis de ESTE parte con la plaga resuelta por el servidor. Sin la
      // migración v2 la columna no existe: error, y se mira el parte.
      const { data: filas, error } = await sb
        .from("pest_photo_analyses")
        .select("id, ai_pest_id, ai_outcome, candidate_mode, result")
        .eq("session_id", sessionId!)
        .not("ai_pest_id", "is", null)
        .order("created_at", { ascending: false })
        .limit(1);
      const fila = !error ? (filas ?? [])[0] ?? null : null;
      return fila ? sugerenciaDe(fila, null) : null;
    },
  });
  // Manda lo que resolvió el servidor; si no hay fila (o la base aún no tiene la columna), el parte.
  return { data: enabled ? query.data ?? respaldo : null, loading: query.isLoading };
}
